import type { ReactNode } from 'react';
import {
  Box, HStack, VStack,
} from '@gluestack-ui/themed';

import { MeetingSpeakerView } from './MeetingVideoTiles';

type MeetingStageProps = {
  /** Banner content shown above the video (e.g. the current exam question). */
  content?: ReactNode;
  contentPlacement?: 'top' | 'side';
  /** Element layered over the speaker view (e.g. a waiting notice). */
  overlay?: ReactNode;
  /** Optional fixed speaker aspect ratio for compact call layouts. */
  speakerAspectRatio?: number;
  speakerMaxWidth?: number;
};

/**
 * Main meeting stage — always camera-primary.
 *
 * Remote screen share never steals the stage (proctoring/share is secondary);
 * {@link MeetingSpeakerView} keeps the remote camera large and overlays share
 * (and self-view) as corner PiPs. The stage fills its parent without overflowing.
 */
export function MeetingStage({
  content,
  contentPlacement = 'top',
  overlay,
  speakerAspectRatio,
  speakerMaxWidth = 560,
}: MeetingStageProps) {
  const speakerView = <MeetingSpeakerView />;

  if (content && contentPlacement === 'side') {
    return (
      <HStack flex={1} space="lg" minHeight={0} testID="meeting-stage-side">
        <Box
          flex={2}
          minWidth={360}
          overflow="hidden"
          borderRadius="$lg"
          bg="$bgSubtle"
          borderWidth={1}
          borderColor="$borderLight"
          testID="meeting-stage-content"
        >
          {content}
        </Box>
        <Box flex={1} minWidth={280} maxWidth={speakerMaxWidth + 48} minHeight={0} position="relative" testID="meeting-stage-speaker">
          <Box flex={1} minHeight={0} alignItems="center" justifyContent="center">
            <Box
              width="100%"
              maxWidth={speakerMaxWidth}
              aspectRatio={speakerAspectRatio ?? 4 / 3}
              position="relative"
            >
              {speakerView}
            </Box>
          </Box>
          {overlay ? (
            <Box position="absolute" top={12} left={0} right={0} alignItems="center" zIndex={20}>
              {overlay}
            </Box>
          ) : null}
        </Box>
      </HStack>
    );
  }

  return (
    <VStack flex={1} space="sm" minHeight={0} testID="meeting-stage">
      {content ? (
        <Box
          flexShrink={0}
          maxHeight="40%"
          overflow="hidden"
          borderRadius="$lg"
          bg="$bgSubtle"
          borderWidth={1}
          borderColor="$borderLight"
          testID="meeting-stage-content"
        >
          {content}
        </Box>
      ) : null}
      <Box flex={1} minHeight={0} position="relative" testID="meeting-stage-speaker">
        {speakerAspectRatio ? (
          <Box flex={1} minHeight={0} alignItems="center" justifyContent="center">
            <Box
              width="100%"
              maxWidth={speakerMaxWidth}
              aspectRatio={speakerAspectRatio}
              position="relative"
            >
              {speakerView}
            </Box>
          </Box>
        ) : speakerView}
        {overlay ? (
          <Box position="absolute" top={12} left={0} right={0} alignItems="center" zIndex={20}>
            {overlay}
          </Box>
        ) : null}
      </Box>
    </VStack>
  );
}
